// =============================================================================
// MeuDriver — Hook: useStudentProgress
// Médias de habilidades e histórico de aulas do aluno logado
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { LessonSkillRatingRow, StudentSkillAverages } from '@/types/database'

export function useStudentSkillAverages(studentId?: string) {
  const { user } = useAuth()
  const uid = studentId ?? user?.id

  return useQuery<StudentSkillAverages | null>({
    queryKey: ['skill-averages', uid],
    queryFn: async () => {
      // CRIT-003: view student_skill_averages removida (expunha dados de todos os
      // alunos sem RLS). Substituída por RPC get_my_skill_averages() que filtra
      // internamente pelo auth.uid() via SECURITY DEFINER.
      const { data, error } = await supabase.rpc('get_my_skill_averages')
      if (error) throw error
      // A RPC retorna array; pega a primeira linha (sempre 1 linha, mesmo sem dados).
      // Injeta student_id para manter compatibilidade com a interface StudentSkillAverages.
      const row = Array.isArray(data) ? (data[0] ?? null) : (data ?? null)
      if (!row) return null
      return { ...row, student_id: uid! } as StudentSkillAverages
    },
    enabled: !!uid,
    staleTime: 1000 * 60 * 5,
  })
}

export interface LessonHistoryEntry {
  id: string
  scheduled_at: string
  duration_minutes: number
  status: string
  instructor_name: string | null
  instructor_avatar: string | null
  skill_rating: LessonSkillRatingRow | null
  instructor_note: string | null
}

export function useStudentLessonHistory(studentId?: string) {
  const { user } = useAuth()
  const uid = studentId ?? user?.id

  return useQuery<LessonHistoryEntry[]>({
    queryKey: ['lesson-history', uid],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          scheduled_at,
          duration_minutes,
          status,
          instructor_note,
          instructor:instructors!bookings_instructor_id_fkey(
            profile:profiles!instructors_profile_id_fkey(
              full_name,
              avatar_url
            )
          ),
          skill_rating:lesson_skill_ratings(*)
        `)
        .eq('student_id', uid!)
        .eq('status', 'completed')
        .order('scheduled_at', { ascending: false })
        .limit(100)

      if (error) throw error

      return ((data ?? []) as any[]).map((b) => ({
        id: b.id,
        scheduled_at: b.scheduled_at,
        duration_minutes: b.duration_minutes,
        status: b.status,
        instructor_name: b.instructor?.profile?.full_name ?? null,
        instructor_avatar: b.instructor?.profile?.avatar_url ?? null,
        skill_rating: Array.isArray(b.skill_rating) ? (b.skill_rating[0] ?? null) : (b.skill_rating ?? null),
        instructor_note: b.instructor_note ?? null,
      })) as LessonHistoryEntry[]
    },
    enabled: !!uid,
    staleTime: 1000 * 60 * 5,
  })
}

export function useStudentProgressSummary(studentId?: string) {
  const { user } = useAuth()
  const uid = studentId ?? user?.id

  return useQuery({
    queryKey: ['progress-summary', uid],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bookings')
        .select('duration_minutes')
        .eq('student_id', uid!)
        .eq('status', 'completed')
      if (error) throw error
      const rows = (data ?? []) as { duration_minutes: number }[]
      const totalMinutes = rows.reduce((sum, r) => sum + (r.duration_minutes ?? 60), 0)
      return {
        totalLessons: rows.length,
        totalHours: Math.round(totalMinutes / 60),
      }
    },
    enabled: !!uid,
    staleTime: 1000 * 60 * 5,
  })
}
